// === Design-token resolution ===
// Glass components need concrete hex values (canvas drawing and [data-color]
// scanning both parse '#RRGGBB'), so the accent is resolved from the live
// cascade at call time instead of being hardcoded. Changing --aero-accent in
// aero-design-system.css therefore propagates to the glass engine too.
// NOTE: --aero-accent must stay a 6-digit hex for hexToRgb() to parse it.
export function resolveAeroToken(name, fallback) {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function aeroAccent() {
  return resolveAeroToken('--aero-accent', '#3D78B0');
}

// === Color utilities ===
export function hexToRgb(hex) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16)
  };
}

export function easeOut(t) {
  return 1 - Math.pow(1 - t, 2.5);
}

// === DOM scanning ===
// Scans all [data-color] elements and returns sources sorted by strength
// relative to a given center point (cx, cy)
export function scanSources(cx, cy, colorElements, radius = 280) {
  const sources = [];
  colorElements.forEach(({ element, color }) => {
    const sr = element.getBoundingClientRect();
    const sx = sr.left + sr.width / 2;
    const sy = sr.top + sr.height / 2;
    const dx = sx - cx;
    const dy = sy - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < radius && dist > 0) {
      sources.push({
        color,
        dx, dy, dist,
        size: Math.max(sr.width, sr.height),
        strength: easeOut(1 - dist / radius)
      });
    }
  });
  return sources.sort((a, b) => b.strength - a.strength);
}

// === Drawing: Combined mode ===
// Draws body tint, edge glow, thickness bevel, and rim shadow
// This is the PRIMARY mode for most buttons
export function drawCombined(ctx, w, h, sources, str, setShadow) {
  ctx.clearRect(0, 0, w, h);
  if (!sources.length) return;

  // Body tint — averaged color from all sources, radial gradient from weighted center
  let tR = 0, tG = 0, tB = 0, tW = 0;
  sources.forEach(s => {
    const wt = s.strength * (s.size / 50);
    tR += s.color.r * wt;
    tG += s.color.g * wt;
    tB += s.color.b * wt;
    tW += wt;
  });
  if (tW > 0) {
    const ar = Math.round(tR / tW), ag = Math.round(tG / tW), ab = Math.round(tB / tW);
    const s0 = sources[0];
    let cx = w / 2, cy = h / 2;
    if (s0) {
      cx += (s0.dx / s0.dist) * w * 0.12;
      cy += (s0.dy / s0.dist) * h * 0.12;
    }
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.75);
    grad.addColorStop(0, `rgba(${ar},${ag},${ab},${str * 0.22})`);
    grad.addColorStop(0.5, `rgba(${ar},${ag},${ab},${str * 0.1})`);
    grad.addColorStop(1, `rgba(${ar},${ag},${ab},${str * 0.03})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }

  // Edge glow — directional colored glow from top 3 sources
  ctx.globalCompositeOperation = 'lighter';
  sources.slice(0, 3).forEach(src => {
    const alpha = src.strength * str * 0.3;
    if (alpha < 0.008) return;
    const { r, g, b } = src.color;
    const dirX = src.dx / src.dist, dirY = src.dy / src.dist;
    const edgeW = Math.max(3, 8 * src.strength);
    if (Math.abs(dirX) > Math.abs(dirY)) {
      const x = dirX > 0 ? w : 0;
      const grad = ctx.createLinearGradient(x, 0, x + (dirX > 0 ? -edgeW : edgeW), 0);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(dirX > 0 ? w - edgeW : 0, 0, edgeW, h);
    } else {
      const y = dirY > 0 ? h : 0;
      const grad = ctx.createLinearGradient(0, y, 0, y + (dirY > 0 ? -edgeW : edgeW));
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, dirY > 0 ? h - edgeW : 0, w, edgeW);
    }
  });
  ctx.globalCompositeOperation = 'source-over';

  // Thickness bevel — thin inner edge from top 2 sources
  sources.slice(0, 2).forEach(src => {
    const alpha = src.strength * str * 0.18;
    if (alpha < 0.008) return;
    const { r, g, b } = src.color;
    const dirX = src.dx / src.dist, dirY = src.dy / src.dist;
    if (Math.abs(dirX) > Math.abs(dirY)) {
      const grad = ctx.createLinearGradient(dirX > 0 ? w : 0, 0, dirX > 0 ? w - 5 : 5, 0);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(dirX > 0 ? w - 5 : 0, 0, 5, h);
    } else {
      const grad = ctx.createLinearGradient(0, dirY > 0 ? h : 0, 0, dirY > 0 ? h - 5 : 5);
      grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, dirY > 0 ? h - 5 : 0, w, 5);
    }
  });

  // Rim shadow — colored glow around the button (applied via DOM)
  if (setShadow) {
    const shadows = [];
    sources.slice(0, 2).forEach(src => {
      const alpha = src.strength * str * 0.18;
      if (alpha < 0.015) return;
      const { r, g, b } = src.color;
      const dx = src.dx / src.dist, dy = src.dy / src.dist;
      shadows.push(`${Math.round(dx * 2)}px ${Math.round(dy * 1.5)}px ${Math.round(3 + src.strength * 5)}px rgba(${r},${g},${b},${alpha})`);
    });
    if (shadows.length) {
      setShadow('0 1px 3px rgba(0,0,0,0.04), ' + shadows.join(', '));
    }
  }
}

// === Drawing: Caustic mode ===
// Draws chromatic aberration edge lines (R/G/B separated)
// Best for: clear/white/grey buttons, or when you want a more dramatic glass effect
export function drawCaustic(ctx, w, h, sources, str, thickness) {
  ctx.clearRect(0, 0, w, h);
  const lineW = (thickness || 2) * 0.7;
  const maxSrc = Math.min(sources.length, 3);

  for (let si = 0; si < maxSrc; si++) {
    const src = sources[si];
    const alpha = src.strength * str * (si === 0 ? 0.65 : 0.25);
    if (alpha < 0.01) continue;
    const { r, g, b } = src.color;

    const dirX = src.dx / src.dist;
    const dirY = src.dy / src.dist;
    const absX = Math.abs(dirX);
    const absY = Math.abs(dirY);
    const isHorizontalEdge = absY >= absX;

    ctx.save();
    ctx.lineCap = 'round';

    // Helper: create a fading gradient stroke along a line
    // Fades from transparent → full color → full color → transparent
    function fadingColor(x1, y1, x2, y2, colorStr) {
      const grad = ctx.createLinearGradient(x1, y1, x2, y2);
      grad.addColorStop(0, colorStr.replace(/[\d.]+\)$/, '0)'));
      grad.addColorStop(0.18, colorStr);
      grad.addColorStop(0.82, colorStr);
      grad.addColorStop(1, colorStr.replace(/[\d.]+\)$/, '0)'));
      return grad;
    }

    const colors = [
      `rgba(${Math.min(255, r + 50)},${Math.max(0, g - 40)},${Math.max(0, b - 40)},${alpha * 0.5})`,
      `rgba(${r},${g},${b},${alpha * 0.7})`,
      `rgba(${Math.max(0, r - 40)},${Math.max(0, g - 40)},${Math.min(255, b + 50)},${alpha * 0.5})`
    ];

    if (isHorizontalEdge) {
      // Draw on top or bottom edge
      const y = dirY > 0 ? h - 0.5 : 0.5;
      const centerX = w * 0.5 + dirX * w * 0.3;
      const segLen = w * 0.7;
      const x1 = Math.max(1, centerX - segLen / 2);
      const x2 = Math.min(w - 1, centerX + segLen / 2);

      colors.forEach((col, i) => {
        const off = (i - 1) * 1.5;
        ctx.strokeStyle = fadingColor(x1, y + off, x2, y + off, col);
        ctx.lineWidth = lineW * (i === 1 ? 1 : 0.5);
        ctx.beginPath();
        ctx.moveTo(x1, y + off);
        ctx.lineTo(x2, y + off);
        ctx.stroke();
      });

      // White specular highlight — also fading
      const specCol = `rgba(255,255,255,${alpha * 0.35})`;
      ctx.strokeStyle = fadingColor(x1 + 2, y, x2 - 2, y, specCol);
      ctx.lineWidth = lineW * 0.25;
      ctx.beginPath();
      ctx.moveTo(x1 + 2, y);
      ctx.lineTo(x2 - 2, y);
      ctx.stroke();

      // Glow — constrained to the faded segment width
      const glow = ctx.createLinearGradient(0, y, 0, y + (dirY > 0 ? -5 : 5));
      glow.addColorStop(0, `rgba(${r},${g},${b},${alpha * 0.12})`);
      glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = glow;
      ctx.fillRect(x1, dirY > 0 ? h - 5 : 0, x2 - x1, 5);
    } else {
      // Draw on left or right edge
      const x = dirX > 0 ? w - 0.5 : 0.5;
      const centerY = h * 0.5 + dirY * h * 0.3;
      const segLen = h * 0.7;
      const y1 = Math.max(1, centerY - segLen / 2);
      const y2 = Math.min(h - 1, centerY + segLen / 2);

      colors.forEach((col, i) => {
        const off = (i - 1) * 1.5;
        ctx.strokeStyle = fadingColor(x + off, y1, x + off, y2, col);
        ctx.lineWidth = lineW * (i === 1 ? 1 : 0.5);
        ctx.beginPath();
        ctx.moveTo(x + off, y1);
        ctx.lineTo(x + off, y2);
        ctx.stroke();
      });

      // White specular highlight — also fading
      const specCol = `rgba(255,255,255,${alpha * 0.35})`;
      ctx.strokeStyle = fadingColor(x, y1 + 2, x, y2 - 2, specCol);
      ctx.lineWidth = lineW * 0.25;
      ctx.beginPath();
      ctx.moveTo(x, y1 + 2);
      ctx.lineTo(x, y2 - 2);
      ctx.stroke();

      // Glow
      const glow = ctx.createLinearGradient(x, 0, x + (dirX > 0 ? -5 : 5), 0);
      glow.addColorStop(0, `rgba(${r},${g},${b},${alpha * 0.12})`);
      glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = glow;
      ctx.fillRect(dirX > 0 ? w - 5 : 0, y1, 5, y2 - y1);
    }

    ctx.restore();
  }
}
